import React, { useEffect, useRef, useState, FC, useCallback } from 'react';
import { Grow, Paper, PaperProps, ClickAwayListener } from '@mui/material';
import Draggable, { ControlPosition } from 'react-draggable';
import styled from 'styled-components';
import { Grabber } from '../draggable-navigation-menu';

const PopoverBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100000;
`;

const DraggablePopover: FC<{
  children: JSX.Element | JSX.Element[];
  anchorEl: Element | null;
  onEntering?: () => void;
  onClose?: () => void;
  paperProps: Partial<PaperProps>;
  handle?: string;
  offset?: { x: number; y: number };
}> = ({
  children,
  anchorEl,
  paperProps,
  onEntering,
  onClose,
  handle,
  offset = { x: 0, y: 0 },
}) => {
  const [displayRoot, setDisplayRoot] = useState<boolean>(false);

  const [originPoint, setOriginPoint] = useState<ControlPosition | undefined>(
    undefined
  );

  const draggableRef = useRef(null);

  const escCallback = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && anchorEl && displayRoot && onClose)
        onClose();
    },
    [anchorEl, displayRoot, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', escCallback, false);

    return () => {
      document.removeEventListener('keydown', escCallback, false);
    };
  }, [escCallback]);

  // Setting origin point only after we have the anchorEl and the components are displayed
  useEffect(() => {
    if (anchorEl && displayRoot) {
      const anchorDOMRect = anchorEl.getBoundingClientRect();

      const contentsWidth = draggableRef.current.offsetWidth;
      const contentsHeight = draggableRef.current.offsetHeight;

      const originX =
        anchorDOMRect.right + contentsWidth + offset.x < window.innerWidth
          ? anchorDOMRect.right + offset.x
          : anchorDOMRect.right +
            offset.x -
            (anchorDOMRect.right +
              offset.x +
              contentsWidth -
              window.innerWidth);
      const originY =
        anchorDOMRect.bottom + offset.y + contentsHeight < window.innerHeight
          ? anchorDOMRect.bottom + offset.y
          : anchorDOMRect.bottom +
            offset.y -
            (anchorDOMRect.bottom +
              offset.y +
              contentsHeight -
              window.innerHeight);

      setOriginPoint({ x: originX, y: originY });
    }
  }, [anchorEl, displayRoot, offset.x, offset.y]);

  useEffect(() => {
    if (anchorEl) setDisplayRoot(true);
    if (!anchorEl && onClose) onClose();
  }, [anchorEl, onClose]);

  // originPoint will prevent dragging if not set to undefined
  useEffect(() => {
    if (originPoint) setOriginPoint(undefined);
  }, [originPoint]);

  return (
    <PopoverBackdrop style={{ display: displayRoot ? 'block' : 'none' }}>
      <Grow
        onEntering={onEntering}
        onExited={() => {
          if (!anchorEl && displayRoot) setDisplayRoot(false);
        }}
        in={!!anchorEl && displayRoot}
        style={{ height: '100%' }}
      >
        <div>
          <Draggable
            position={originPoint}
            bounds="parent"
            nodeRef={draggableRef}
            handle={handle ?? null}
          >
            <Paper ref={draggableRef} {...paperProps}>
              <ClickAwayListener
                onClickAway={() => {
                  if (anchorEl && displayRoot && onClose) onClose();
                }}
              >
                <div>
                  {Grabber ? <Grabber /> : null}
                  {children}
                </div>
              </ClickAwayListener>
            </Paper>
          </Draggable>
        </div>
      </Grow>
    </PopoverBackdrop>
  );
};

export default DraggablePopover;
